import type { Options } from '../config/config';
import { ACTION_LABELS, type Action, type Bindings, keyLabel } from '../core/input';

export type Screen = 'none' | 'title' | 'pause' | 'settings' | 'gameover' | 'clear';

export interface OverlayHandlers {
  start(): void;
  resume(): void;
  restartLevel(): void;
  quit(): void;
  retry(): void;
  next(): void;
  openSettings(): void;
  closeSettings(): void;
  clearSave(): void;
  resetBinds(): void;
  optionChanged(key: keyof Options, value: unknown): void;
  rebind(action: Action, done: () => void): void;
}

/**
 * DOM overlay: title, pause, settings, game over and results screens.
 *
 * Menus are regular HTML so they are accessible and easy to restyle; the game itself is
 * drawn entirely on the canvas underneath.
 */
export class Overlay {
  private panels = new Map<Screen, HTMLElement>();
  private root: HTMLElement;
  private screen: Screen = 'none';
  private returnScreen: Screen = 'title';

  constructor(
    root: HTMLElement,
    private h: OverlayHandlers,
  ) {
    this.root = root;
    root.querySelectorAll<HTMLElement>('[data-screen]').forEach((el) => {
      this.panels.set(el.dataset.screen as Screen, el);
    });
    root.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!btn) return;
      this.onAction(btn.dataset.action ?? '');
    });
  }

  private onAction(action: string): void {
    switch (action) {
      case 'start':
        this.h.start();
        break;
      case 'resume':
        this.h.resume();
        break;
      case 'restart-level':
        this.h.restartLevel();
        break;
      case 'quit':
        this.h.quit();
        break;
      case 'retry':
        this.h.retry();
        break;
      case 'next':
        this.h.next();
        break;
      case 'settings':
        this.returnScreen = this.screen === 'pause' ? 'pause' : 'title';
        this.h.openSettings();
        break;
      case 'close-settings':
        this.h.closeSettings();
        break;
      case 'clear-save':
        this.h.clearSave();
        break;
      case 'reset-binds':
        this.h.resetBinds();
        break;
    }
  }

  get settingsReturn(): Screen {
    return this.returnScreen;
  }

  get current(): Screen {
    return this.screen;
  }

  show(screen: Screen): void {
    this.screen = screen;
    for (const [name, el] of this.panels) el.classList.toggle('active', name === screen);
    this.root.classList.toggle('hidden', screen === 'none');
  }

  setText(bind: string, text: string): void {
    const el = this.root.querySelector<HTMLElement>(`[data-bind="${bind}"]`);
    if (el) el.textContent = text;
  }

  setGameOver(title: string, score: number, hi: number): void {
    this.setText('goTitle', title);
    this.setText('goScore', String(score));
    this.setText('goHi', String(hi));
  }

  setClear(title: string, rows: [string, string][]): void {
    this.setText('clearTitle', title);
    const list = this.root.querySelector<HTMLElement>('[data-bind="tally"]');
    if (!list) return;
    list.innerHTML = '';
    rows.forEach(([label, value], i) => {
      const li = document.createElement('li');
      if (i === rows.length - 1) li.className = 'total';
      const l = document.createElement('span');
      l.textContent = label;
      const v = document.createElement('b');
      v.textContent = value;
      li.append(l, v);
      list.append(li);
    });
  }

  /** Wire the option controls to the live option object. */
  bindOptions(options: Options): void {
    const q = <T extends HTMLElement>(name: string) =>
      this.root.querySelector<T>(`[data-bind="${name}"]`);

    const check = (name: keyof Options) => {
      const el = q<HTMLInputElement>(name);
      if (!el) return;
      el.checked = Boolean(options[name]);
      el.onchange = () => this.h.optionChanged(name, el.checked);
    };
    check('musicOn');
    check('sfxOn');
    check('checkpoints');
    check('backtrack');

    const range = (name: 'musicVol' | 'sfxVol', textName: string) => {
      const el = q<HTMLInputElement>(name);
      const txt = q<HTMLElement>(textName);
      if (!el) return;
      el.value = String(options[name]);
      if (txt) txt.textContent = `${options[name]}%`;
      el.oninput = () => {
        const v = Number(el.value);
        if (txt) txt.textContent = `${v}%`;
        this.h.optionChanged(name, v);
      };
    };
    range('musicVol', 'musicVolText');
    range('sfxVol', 'sfxVolText');

    const select = (name: 'touchMode' | 'scaleMode') => {
      const el = q<HTMLSelectElement>(name);
      if (!el) return;
      el.value = String(options[name]);
      el.onchange = () => this.h.optionChanged(name, el.value);
    };
    select('touchMode');
    select('scaleMode');
  }

  /** Rebuild the key binding list. */
  renderBindings(bindings: Bindings): void {
    const host = this.root.querySelector<HTMLElement>('[data-bind="binds"]');
    if (!host) return;
    host.innerHTML = '';
    (Object.keys(ACTION_LABELS) as Action[]).forEach((action) => {
      const row = document.createElement('div');
      row.className = 'bind';
      const label = document.createElement('span');
      label.textContent = ACTION_LABELS[action];
      const btn = document.createElement('button');
      btn.textContent = bindings[action].map(keyLabel).join(' / ');
      btn.onclick = () => {
        btn.classList.add('listening');
        btn.textContent = '按键…';
        this.h.rebind(action, () => {
          btn.classList.remove('listening');
          this.renderBindings(bindings);
        });
      };
      row.append(label, btn);
      host.append(row);
    });
  }
}
